import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { useAuthStore } from '@/stores/auth-store';
import { useDataStore } from '@/stores/data-store';
import { getRepository, setRepositoryMode } from '@/data/repository-factory';
import type {
  TempFoyer,
  TempBlock,
  TempDomain,
  TempCategory,
  TempLine,
  TempObjective,
  TempScenario,
} from '@/stores/temp-types';
import type {
  BudgetSnapshot,
  PatrimoineCategory,
  PatrimoineLine,
  PatrimoineSnapshot,
  KPIThresholds,
} from '@/engine/types';

// ─── BOOTSTRAP STATE (exposed for AppShell guard) ───────────────────────────

interface BootstrapState {
  ready: boolean;
  hasFoyer: boolean;
  mode: 'local' | 'cloud';
  setReady: (ready: boolean) => void;
  setResult: (hasFoyer: boolean, mode: 'local' | 'cloud') => void;
}

export const useBootstrapStore = create<BootstrapState>((set) => ({
  ready: false,
  hasFoyer: false,
  mode: 'local',
  setReady: (ready) => set({ ready }),
  setResult: (hasFoyer, mode) => set({ hasFoyer, mode }),
}));

// Module-level guard so bootstrap runs only once per app lifetime
// (useRef per-component would re-run if App unmounts/remounts in StrictMode).
let bootstrapStarted = false;

// ─── HYDRATATION (exportée pour LoginScreen / JoinFoyerScreen) ──────────────

/**
 * Hydrate the data store from a foyer by loading every related collection via
 * the active repository. Each collection load is wrapped in its own try/catch
 * so one failure does not prevent the others from loading.
 *
 * The caller is responsible for having called `setRepositoryMode()` first so
 * that `getRepository()` returns the right instance.
 */
export async function bootstrapFromFoyer(foyer: TempFoyer): Promise<void> {
  const repo = getRepository();
  const store = useDataStore.getState();

  store.setFoyer(foyer);

  try {
    const blocks = (await repo.getBlocks(foyer.id)) as TempBlock[];
    store.setBlocks(blocks);
  } catch (err) {
    console.warn('[bootstrap] getBlocks failed', err);
  }

  try {
    const domains = (await repo.getDomains(foyer.id)) as TempDomain[];
    store.setDomains(domains);
  } catch (err) {
    console.warn('[bootstrap] getDomains failed', err);
  }

  try {
    const categories = (await repo.getCategories(foyer.id)) as TempCategory[];
    store.setCategories(categories);
  } catch (err) {
    console.warn('[bootstrap] getCategories failed', err);
  }

  try {
    const lines = (await repo.getLines(foyer.id)) as TempLine[];
    store.setLines(lines);
  } catch (err) {
    console.warn('[bootstrap] getLines failed', err);
  }

  try {
    const objectives = (await repo.getObjectives(foyer.id)) as TempObjective[];
    store.setObjectives(objectives);
  } catch (err) {
    console.warn('[bootstrap] getObjectives failed', err);
  }

  try {
    const scenarios = (await repo.getScenarios(foyer.id)) as TempScenario[];
    store.setScenarios(scenarios);
  } catch (err) {
    console.warn('[bootstrap] getScenarios failed', err);
  }

  try {
    const snapshots = (await repo.getBudgetSnapshots(foyer.id)) as BudgetSnapshot[];
    store.setBudgetSnapshots(snapshots);
  } catch (err) {
    console.warn('[bootstrap] getBudgetSnapshots failed', err);
  }

  try {
    const patCats = (await repo.getPatrimoineCategories(foyer.id)) as PatrimoineCategory[];
    store.setPatrimoineCategories(patCats);
  } catch (err) {
    console.warn('[bootstrap] getPatrimoineCategories failed', err);
  }

  try {
    const patLines = (await repo.getPatrimoineLines(foyer.id)) as PatrimoineLine[];
    store.setPatrimoineLines(patLines);
  } catch (err) {
    console.warn('[bootstrap] getPatrimoineLines failed', err);
  }

  try {
    const patSnaps = (await repo.getPatrimoineSnapshots(foyer.id)) as PatrimoineSnapshot[];
    store.setPatrimoineSnapshots(patSnaps);
  } catch (err) {
    console.warn('[bootstrap] getPatrimoineSnapshots failed', err);
  }

  try {
    const thresholds = (await repo.getKpiThresholds(foyer.id)) as KPIThresholds | null;
    if (thresholds) store.setKpiThresholds(thresholds);
  } catch (err) {
    console.warn('[bootstrap] getKpiThresholds failed', err);
  }
}

// ─── useBootstrap HOOK (mounted once at App level) ──────────────────────────

export interface UseBootstrapResult {
  ready: boolean;
  hasFoyer: boolean;
  mode: 'local' | 'cloud';
}

/**
 * Runs once per app mount. Tries cloud session first, falls back to local IDB.
 * Exposes `{ ready, hasFoyer, mode }` and hydrates `useDataStore` when a foyer
 * is found. `ready` flips to true at the end regardless of outcome so the App
 * can unmask its router.
 */
export function useBootstrap(): UseBootstrapResult {
  const ready = useBootstrapStore((s) => s.ready);
  const hasFoyer = useBootstrapStore((s) => s.hasFoyer);
  const mode = useBootstrapStore((s) => s.mode);
  const startedRef = useRef(false);

  useEffect(() => {
    if (bootstrapStarted || startedRef.current) return;
    bootstrapStarted = true;
    startedRef.current = true;

    const { setReady, setResult } = useBootstrapStore.getState();

    (async () => {
      // (a) Try cloud session first
      let cloudUser = null;
      try {
        cloudUser = await useAuthStore.getState().fetchCurrentUser();
      } catch (err) {
        console.warn('[bootstrap] fetchCurrentUser failed', err);
      }

      if (cloudUser) {
        setRepositoryMode('cloud');
        let cloudFoyer: TempFoyer | null = null;
        try {
          cloudFoyer = (await getRepository().getFoyer()) as TempFoyer | null;
        } catch (err) {
          // 404 (no foyer yet) or any other cloud error — treat as no foyer.
          console.warn('[bootstrap] cloud getFoyer failed', err);
          cloudFoyer = null;
        }

        if (cloudFoyer) {
          try {
            await bootstrapFromFoyer(cloudFoyer);
          } catch (err) {
            console.warn('[bootstrap] hydration (cloud) failed', err);
          }
          setResult(true, 'cloud');
        } else {
          setResult(false, 'cloud');
        }
        setReady(true);
        return;
      }

      // (b) Fall back to local IDB
      setRepositoryMode('local');
      let localFoyer: TempFoyer | null = null;
      try {
        localFoyer = (await getRepository().getFoyer()) as TempFoyer | null;
      } catch (err) {
        console.warn('[bootstrap] local getFoyer failed', err);
        localFoyer = null;
      }

      if (localFoyer) {
        try {
          await bootstrapFromFoyer(localFoyer);
        } catch (err) {
          console.warn('[bootstrap] hydration (local) failed', err);
        }
        setResult(true, 'local');
      } else {
        setResult(false, 'local');
      }
      setReady(true);
    })();
  }, []);

  return { ready, hasFoyer, mode };
}
